import { DateTime } from 'luxon';

import { DailyCount, TimeRange, TopModelsByDay } from '@/types/observability';

export type Granularity = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'half-yearly' | 'yearly';

export function formatChartDate( dateStr: string ): string {
  return DateTime.fromISO( dateStr, { zone: 'utc' } ).toLocaleString( { month: 'short', day: 'numeric' } );
}

function getQuarterStartMonth( month: number ): number {
  return Math.floor( ( month - 1 ) / 3 ) * 3 + 1;
}

function getBucketDate( date: string, granularity: Granularity ): string {
  const dt = DateTime.fromISO( date, { zone: 'utc' } );
  switch ( granularity ) {
    case 'daily': return dt.toISODate()!;
    case 'weekly': return dt.startOf( 'week' ).toISODate()!;
    case 'monthly': return dt.startOf( 'month' ).toISODate()!;
    case 'quarterly': return dt.set( { month: getQuarterStartMonth( dt.month ), day: 1 } ).toISODate()!;
    case 'half-yearly': return dt.set( { month: dt.month <= 6 ? 1 : 7, day: 1 } ).toISODate()!;
    case 'yearly': return dt.startOf( 'year' ).toISODate()!;
  }
}

export function pickGranularity( data: DailyCount[], timeRange: TimeRange ): Granularity {
  if ( timeRange === '7d' ) return 'daily';
  if ( timeRange === '30d' ) return 'weekly';

  // 'all': pick finest granularity that yields ≤ 10 points
  if ( data.length <= 10 ) return 'daily';

  const weekCount = new Set( data.map( d => getBucketDate( d.date, 'weekly' ) ) ).size;
  if ( weekCount <= 10 ) return 'weekly';

  const monthCount = new Set( data.map( d => getBucketDate( d.date, 'monthly' ) ) ).size;
  if ( monthCount <= 10 ) return 'monthly';

  const quarterCount = new Set( data.map( d => getBucketDate( d.date, 'quarterly' ) ) ).size;
  if ( quarterCount <= 10 ) return 'quarterly';

  const halfYearCount = new Set( data.map( d => getBucketDate( d.date, 'half-yearly' ) ) ).size;
  if ( halfYearCount <= 10 ) return 'half-yearly';

  return 'yearly';
}

export function aggregateDailyCountsByGranularity( data: DailyCount[], granularity: Granularity ): DailyCount[] {
  if ( granularity === 'daily' ) return data;

  const bucketMap = new Map<string, DailyCount>();

  for ( const entry of data ) {
    const key = getBucketDate( entry.date, granularity );
    const existing = bucketMap.get( key );
    if ( existing ) {
      existing.count += entry.count;
    } else {
      bucketMap.set( key, { date: getBucketDate( entry.date, granularity ), count: entry.count } );
    }
  }

  return Array.from( bucketMap.values() ).sort( ( a, b ) => a.date.localeCompare( b.date ) );
}

export function aggregateTopModelsByGranularity( data: TopModelsByDay[], granularity: Granularity ): TopModelsByDay[] {
  if ( granularity === 'daily' ) return data;

  type Accumulator = { date: string; models: Map<string, { sum: number; days: number; count: number }> };
  const bucketMap = new Map<string, Accumulator>();

  for ( const entry of data ) {
    const key = getBucketDate( entry.date, granularity );
    if ( !bucketMap.has( key ) ) {
      bucketMap.set( key, { date: getBucketDate( entry.date, granularity ), models: new Map() } );
    }
    const bucket = bucketMap.get( key )!;
    for ( const { model, percentage, count } of entry.models ) {
      const acc = bucket.models.get( model );
      if ( acc ) {
        acc.sum += percentage;
        acc.days += 1;
        acc.count += count;
      } else {
        bucket.models.set( model, { sum: percentage, days: 1, count } );
      }
    }
  }

  return Array.from( bucketMap.values() )
    .sort( ( a, b ) => a.date.localeCompare( b.date ) )
    .map( bucket => ( {
      date: bucket.date,
      models: Array.from( bucket.models.entries() ).map( ( [ model, { sum, days, count } ] ) => ( {
        model,
        count,
        percentage: sum / days
      } ) )
    } ) );
}
